// NOMBASE — Idempotency-Key helper for the 7 PR-12-protected mutation routes.
//
// Why this exists:
//   The backend (PR-12) records the response of every protected mutation
//   keyed by (tenant, route, Idempotency-Key) for 24h. If the client
//   sends the SAME key on a retry, the backend replays the original
//   response instead of re-executing — so a flaky network or a
//   user-double-click can never double-receive / double-send / double-PO.
//
// Contract (locked by tests in __tests__/lib/idempotency.test.js):
//   • newIdempotencyKey()
//       Returns a fresh UUID v4 string. One per LOGICAL user action.
//
//   • idempotentFetch(url, options, {
//         idempotencyKey,  // required (callers generate via newIdempotencyKey)
//         retries        = 2,
//         retryDelayMs   = 300,
//         shouldRetry    = (err) => err.name === 'TypeError' || err.name === 'AbortError',
//     })
//       Wraps `fetch`. Adds `Idempotency-Key: <uuid>` to headers.
//       Retries network-layer failures up to `retries` times reusing the
//       SAME key. HTTP error responses (4xx/5xx) are NEVER retried — the
//       caller decides what to do; the backend will replay any cached
//       response under the same key automatically.
//
// What this helper is intentionally NOT:
//   • A general-purpose fetch wrapper. apiRequest() in services/api.js
//     stays the default for non-protected routes.
//   • A queue / offline buffer. PR-12 covers replay safety on retry,
//     not durability across page reloads.
//   • A header-stamper for non-protected routes. Stamping the header
//     where the backend doesn't expect it is harmless but adds noise.

// UUID v4 generator. Prefers the platform `crypto.randomUUID()` (every
// browser since Chrome 92 / Safari 15.4 / Firefox 95 has it). Falls
// back to a Math.random-based v4 — fine for an Idempotency-Key, where
// the only guarantee we need is "this client never reuses a value
// across distinct logical actions". A non-crypto fallback is NOT used
// for security secrets — strictly for retry-correlation IDs.
function _uuidv4Fallback() {
    // Per RFC 4122 §4.4 layout (xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx).
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0;
        const v = c === 'x' ? r : (r & 0x3) | 0x8;
        return v.toString(16);
    });
}

export function newIdempotencyKey() {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID();
    }
    return _uuidv4Fallback();
}

const _defaultShouldRetry = (err) =>
    err && (err.name === 'TypeError' || err.name === 'AbortError');

export async function idempotentFetch(url, options = {}, extras = {}) {
    const {
        idempotencyKey,
        retries = 2,
        retryDelayMs = 300,
        shouldRetry = _defaultShouldRetry,
    } = extras;

    if (!idempotencyKey || typeof idempotencyKey !== 'string') {
        // Programmer error — fail loud at the call site rather than
        // letting a header-less request slip through and lose
        // idempotency protection.
        throw new Error(
            'idempotentFetch: idempotencyKey is required (use newIdempotencyKey()).',
        );
    }

    const headers = {
        ...(options.headers || {}),
        'Idempotency-Key': idempotencyKey,
    };

    let lastErr;
    for (let attempt = 0; attempt <= retries; attempt += 1) {
        try {
            // Success or HTTP-error response — return as-is. Backend
            // replays cached response on retry of same key, so we let
            // the caller see the real status code from each attempt.
            return await fetch(url, { ...options, headers });
        } catch (err) {
            lastErr = err;
            if (attempt >= retries || !shouldRetry(err)) {
                throw err;
            }
            // Linear backoff. Keep it short — PR-12's stale-claim
            // takeover is 60s, so we never want our retries to outlive
            // the in-flight claim window.
            // eslint-disable-next-line no-await-in-loop
            await new Promise((r) => setTimeout(r, retryDelayMs));
        }
    }
    throw lastErr; /* unreachable — kept for typing */
}

// Convenience: POST JSON with idempotency. Most call sites in the app
// use raw `fetch(url, { method, headers, body })` for the 7 protected
// routes; this keeps the call shape compact.
export async function idempotentPostJson(url, body, {
    idempotencyKey,
    authHeader,
    extraHeaders = {},
    ...rest
} = {}) {
    return idempotentFetch(
        url,
        {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                ...(authHeader ? { Authorization: authHeader } : {}),
                ...extraHeaders,
            },
            body: body === undefined ? undefined : JSON.stringify(body),
        },
        { idempotencyKey, ...rest },
    );
}
